import { useEffect, useState } from 'react';
import { APP_VERSION } from '@hts/shared';

interface VersionManifest {
  appVersion: string;
  builtAt: string;
}

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Compares the SWA bundle's embedded APP_VERSION against the currently
 * deployed `/version.json`. Returns `true` when the user is running a
 * stale bundle (e.g. a tab left open across a deploy) so the UI can
 * prompt a reload. Polls on mount, on a fixed interval, and when the
 * tab regains focus — the visibility check catches the most common
 * "left the tab open overnight" pattern with one cheap fetch.
 */
export function useBundleFreshness(): { stale: boolean; latestVersion: string | null } {
  const [stale, setStale] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);

  useEffect(() => {
    // Dev builds advertise themselves as 'v0.0.0-dev' — skip the check.
    if (APP_VERSION === 'v0.0.0-dev') return;

    let cancelled = false;

    const check = async () => {
      try {
        const res = await fetch('/version.json', { cache: 'no-store' });
        if (!res.ok) return;
        const data = (await res.json()) as VersionManifest;
        if (cancelled || !data?.appVersion) return;
        setLatestVersion(data.appVersion);
        if (data.appVersion !== APP_VERSION) setStale(true);
      } catch {
        // Network blip — quietly try again next interval.
      }
    };

    check();
    const interval = setInterval(check, POLL_INTERVAL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return { stale, latestVersion };
}
